import type { PreviewUrl, SocialText, VisualData } from '@adplay/common'
import { type QueryDocumentSnapshot, type SnapshotOptions, Timestamp } from 'firebase/firestore'
import { getStorageJson, saveStorageJSON } from '~/libs/helper/storage-json'
import compactObject from '~/libs/helper/compactObject'

interface ProjectParams {
  id?: string
  teamId: string
  productId?: string
  brandId?: string
  adTemplateId: string
  name: string
  previewUrls: Array<PreviewUrl>
  renderingStatus?: RENDERING_STATUS
  adTexts: AdText[]

  socialText: SocialText
  socialTexts: SocialText[]
  pageIds?: Array<string>

  createdAt: Date
  updatedAt: Date
}

export interface AdText {
  pageId: string

  // text
  primaryText: string
  headline: string
  linkDescription: string
  callToAction: string
}

export enum RENDERING_STATUS {
  RENDERING = 'rendering',
  DONE = 'done',
  FAILED = 'failed',
}

export function defaultAdText(pageId: string): AdText {
  return {
    pageId,
    primaryText: '',
    headline: '',
    linkDescription: '',
    callToAction: 'Learn more',
  }
}

export function defaultSocialText(socialId: string): SocialText {
  return {
    socialId,
    captionHTML: '',
    active: true,
  }
}

export function getProjectContentPath(teamId: string, projectId: string) {
  const runtimeConfig = useRuntimeConfig()

  const bucket = runtimeConfig.public.storageBucket

  return `gs://${bucket}/teams/${teamId}/projects/${projectId}/content.json`
}

export class Project {
  id?: string
  teamId: string
  adTemplateId: string
  productId?: string
  brandId?: string

  name: string
  previewUrls: Array<PreviewUrl>
  renderingStatus?: RENDERING_STATUS

  adTexts: Array<AdText>

  socialText: SocialText
  socialTexts: Array<SocialText>
  pageIds: Array<string>

  visualData?: VisualData

  createdAt: Date
  updatedAt: Date

  static toFirestore(project: Project) {
    return compactObject({
      id: project.id || '',
      teamId: project.teamId || '',
      productId: project.productId || '',
      brandId: project.brandId || '',
      adTemplateId: project.adTemplateId || '',
      name: project.name || '',
      previewUrls: project.previewUrls || [],
      adTexts: project.adTexts || [],
      renderingStatus: project.renderingStatus,
      pageIds: project.pageIds || [],

      socialText: project.socialText || defaultSocialText('all'),
      socialTexts: project.socialTexts || [],

      createdAt: Timestamp.fromDate(project.createdAt || new Date()),
      updatedAt: Timestamp.fromDate(project.updatedAt || new Date()),
    })
  }

  static fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): Project {
    const data = snapshot.data()

    return new Project({
      id: snapshot.id,
      teamId: data.teamId,
      adTemplateId: data.adTemplateId || data.visualTemplateId, // support legacy
      productId: data.productId,
      brandId: data.brandId,
      name: data.name,
      previewUrls: data.previewUrls,
      adTexts: data.adTexts || [],
      renderingStatus: data.renderingStatus,
      pageIds: data.pageIds || [],

      socialText: data.socialText || defaultSocialText('all'),
      socialTexts: data.socialTexts || [],

      createdAt: data.createdAt.toDate ? data.createdAt.toDate() : new Date(),
      updatedAt: data.updatedAt.toDate ? data.updatedAt.toDate() : new Date(),
    })
  }

  static defaultProject(teamId: string, adTemplateId: string, productId?: string, brandId?: string): Project {
    return new Project({
      teamId,
      name: 'Untitled Project',
      adTemplateId,
      productId,
      brandId,
      adTexts: [],
      socialText: defaultSocialText('all'),
      socialTexts: [],
      previewUrls: [],
      pageIds: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    })
  }

  constructor(options: ProjectParams) {
    this.id = options.id
    this.teamId = options.teamId
    this.adTemplateId = options.adTemplateId
    this.productId = options.productId
    this.brandId = options.brandId
    this.name = options.name
    this.previewUrls = options.previewUrls
    this.renderingStatus = options.renderingStatus
    this.adTexts = options.adTexts
    this.pageIds = options.pageIds || []

    this.socialText = options.socialText
    this.socialTexts = options.socialTexts

    this.createdAt = options.createdAt
    this.updatedAt = options.updatedAt
  }

  async getVisualData(force: boolean = false) {
    if (!this.id)
      throw 'Project ID is not set'

    if (this.visualData && !force)
      return this.visualData

    const contentPath = getProjectContentPath(this.teamId, this.id)

    const data = await getStorageJson<VisualData>(contentPath)

    if (typeof data?.content === 'string')
      data.content = JSON.parse(data.content)

    this.visualData = data

    return data
  }

  async saveVisualData(data: VisualData) {
    if (!this.id)
      throw 'Ad ID is not set'

    const contentPath = getProjectContentPath(this.teamId, this.id)

    return saveStorageJSON(contentPath, data)
  }

  toJSON(): ConstructorParameters<typeof Project>[0] {
    return {
      id: this.id,
      teamId: this.teamId,
      productId: this.productId,
      brandId: this.brandId,
      adTemplateId: this.adTemplateId,
      name: this.name,
      previewUrls: this.previewUrls,
      renderingStatus: this.renderingStatus,
      adTexts: this.adTexts,
      pageIds: this.pageIds || [],

      socialText: this.socialText,
      socialTexts: this.socialTexts,

      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    }
  }
}
